import { Region } from '@kaizenplatform/hugot-entity';
import { Meta } from '@kaizenplatform/hugot-entity/dist/entity/DynamicSchemaDefinition/IDynamicSchemaSection';

import { ValueOf } from '../utilityTypes/ValueOf';

export type SearchAssetType = 'video' | 'image' | 'audio';
export type SearchAssetService = 'unsplash' | 'pexels' | 'pixabay' | 'storyblocks';
export type Tempo = 'mid' | 'low' | 'high';

export type SearchAssetQuery = {
  type: SearchAssetType;
  service: SearchAssetService | null;
  query: string;
  page?: number;
  region: Region;
  reset: boolean;
};

type SearchAssetVideo = {
  type: 'video';
  originalUrl: string;
  lightWeightUrl: string;
  thumbnailUrl: string;
  profileUrl?: undefined;
  photographerName?: undefined;
  tempo?: undefined;
  fileName?: undefined;
  displayName?: undefined;
};

type SearchAssetImage = {
  type: 'image';
  originalUrl: string;
  lightWeightUrl: string;
  thumbnailUrl?: undefined;
  profileUrl?: string;
  photographerName?: string;
  tempo?: undefined;
  fileName?: undefined;
  displayName?: undefined;
};

type SearchAssetAudio = {
  type: 'audio';
  originalUrl: string;
  lightWeightUrl?: string;
  thumbnailUrl?: undefined;
  profileUrl?: undefined;
  photographerName?: undefined;
  tempo: Tempo;
  fileName?: string;
  displayName?: string;
};

export type SearchAssetResult = ReadonlyArray<SearchAssetVideo | SearchAssetImage | SearchAssetAudio>;

export const materialTypeDefinitions = {
  video: { name: 'video', index: 0, searchService: ['pixabay', 'pexels'], tempo: undefined },
  image: { name: 'image', index: 1, searchService: ['pixabay', 'unsplash', 'pexels'], tempo: undefined },
  audio: { name: 'audio', index: 2, searchService: ['storyblocks'], tempo: ['high', 'mid', 'low'] },
} as const;

export type MaterialTypeName = keyof typeof materialTypeDefinitions;
export type MaterialTypeDefinition = ValueOf<typeof materialTypeDefinitions>;

export const materialTypeOrder: ReadonlyArray<MaterialTypeName> = ['video', 'image', 'audio'];

export type MaterialTypeOption = {
  [key in MaterialTypeName]: {
    enableSearch: boolean;
    enableRecommend: boolean;
  };
} & {
  isRequired: boolean;
  // NOTE: Epic 2
  // isAllowLeaveToTheCreators: boolean;
};

export const metaToMaterialTypeOption = (meta: Meta): MaterialTypeOption => {
  const defaultOption = {
    video: {
      enableSearch: false,
      enableRecommend: false,
    },
    image: {
      enableSearch: false,
      enableRecommend: false,
    },
    audio: {
      enableSearch: false,
      enableRecommend: false,
    },
  };

  const isRequired = meta.isRequired != null ? meta.isRequired : false;
  const option =
    meta.allowSearchTypes == null
      ? defaultOption
      : meta.allowSearchTypes.reduce((acc: Omit<MaterialTypeOption, 'isRequired'>, cur) => {
          return {
            ...acc,
            [cur]: {
              enableSearch: true,
              enableRecommend: meta.allowRecommendTypes != null ? meta.allowRecommendTypes.includes(cur) : false,
            },
          };
        }, defaultOption);

  return {
    ...option,
    isRequired,
  };
};

export const recommendSearchTexts = {
  jp: {
    video: [
      '人',
      '女性',
      '男性',
      'ビジネスパーソン',
      '子供',
      '家族',
      'ビジネス',
      '仕事',
      'パソコン',
      '背景',
      'インターネット',
      '悩み',
      '考える',
    ],
    image: [
      '人',
      '女性',
      '男性',
      'ビジネスパーソン',
      '子供',
      '家族',
      'ビジネス',
      '仕事',
      'パソコン',
      '背景',
      'インターネット',
      '悩み',
      '考える',
    ],
    audio: [
      'かわいい',
      'かっこいい',
      'カントリー',
      'きれい',
      'クラシック',
      'ジャズ',
      'デジタル',
      'ムーディ',
      'ラグジュアリー',
      'リゾート',
      '季節',
      '爽やか',
    ],
  },
  us: {
    video: [],
    image: [],
    audio: [
      'Cool',
      'Cute',
      'Country',
      'Beautiful',
      'Classic',
      'Jazz',
      'Digital',
      'Moody',
      'Luxury',
      'Resort',
      'Season',
      'Fresh',
    ],
  },
  gl: {
    video: [],
    image: [],
    audio: [
      'Cool',
      'Cute',
      'Country',
      'Beautiful',
      'Classic',
      'Jazz',
      'Digital',
      'Moody',
      'Luxury',
      'Resort',
      'Season',
      'Fresh',
    ],
  },
} as const;
